import { CSSProperties, Fragment, FunctionComponent, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { observer } from "mobx-react-lite";

import { v4 as uuidv4 } from "uuid";

import { classNames } from "primereact/utils";

import { faFile, IconDefinition } from "@fortawesome/pro-light-svg-icons";
import { faCloudArrowUp } from "@fortawesome/pro-regular-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";

import { translationKeys } from "../../../../models/app-translation-keys";
import { UploadFile } from "../../../../models/classes/upload-file.class";
import { FileUploadError } from "../../../../models/enums/file-upload-error.enum";
import { IFileType, possibleFileTypes } from "../../Models/Constants/wellKnownFileTypes.const";

import "./FileUploadArea.scss";

const allowedFileTypesFallback: IFileType[] = [
  possibleFileTypes.video,
  possibleFileTypes.audio,
  possibleFileTypes.jpeg,
  possibleFileTypes.jpg,
  possibleFileTypes.png,
  possibleFileTypes.gif,
  possibleFileTypes.pdf,
  possibleFileTypes.doc,
  possibleFileTypes.docx,
  possibleFileTypes.xls,
  possibleFileTypes.xlsx,
  possibleFileTypes.txt,
  possibleFileTypes.csv,
  possibleFileTypes.mp3,
  possibleFileTypes.wav,
  possibleFileTypes.mp4,
  possibleFileTypes.avi,
  possibleFileTypes.mov,
];

interface IFileUploadAreaProps {
  disabled?: boolean;
  uploadFiles: UploadFile[];
  customDescriptionText?: string;
  showAllowedFileTypes?: boolean;
  existingFilesNames?: string[];
  allowedFileTypes?: IFileType[];
  fileSizeLimitMB: number;
  fileSizeLimitText: string;
  isSingleFileUpload?: boolean;
  maxConcurrentProcessedFiles?: number;
  smallUploadArea?: boolean;
  styles?: CSSProperties;
  onFilesSelected: (files: UploadFile[]) => void;
}

const RawFileUploadArea: FunctionComponent<IFileUploadAreaProps> = (props: IFileUploadAreaProps) => {
  const { t } = useTranslation();
  const inputRef = useRef<HTMLInputElement>(null);
  const [isDragOver, setIsDragOver] = useState<boolean>(false);

  const allowedFileTypes = props.allowedFileTypes ?? allowedFileTypesFallback;

  const isUploadAreaDisabled =
    !!props.disabled ||
    (!!props.isSingleFileUpload && !!props.uploadFiles.length) ||
    (!!props.maxConcurrentProcessedFiles && props.uploadFiles?.length >= props.maxConcurrentProcessedFiles);

  const handleFileChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    if (isUploadAreaDisabled) {
      return;
    }

    const selectedFiles = event.target.files;
    if (selectedFiles && selectedFiles.length > 0) {
      const newFiles = Array.from(selectedFiles) as File[];
      const newUploadFiles = paresFiles(newFiles);
      const validatedFiles = validateFiles(newUploadFiles, props.existingFilesNames);
      props.onFilesSelected(validatedFiles);
    }
    resetSelectedFileInput(event);
  };

  const handleDragEnter = (event: React.DragEvent<HTMLDivElement>) => {
    event.preventDefault();
    if (!isUploadAreaDisabled) {
      setIsDragOver(true);
    }
  };

  const handleDragLeave = (event: React.DragEvent<HTMLDivElement>) => {
    // only clear the state when the cursor actually exits the drop zone entirely
    if (!event.currentTarget.contains(event.relatedTarget as Node)) {
      setIsDragOver(false);
    }
  };

  const handleKeyDown = (event: React.KeyboardEvent<HTMLDivElement>) => {
    if (event.key === "Enter" || event.key === " ") {
      event.preventDefault();
      inputRef.current?.click();
    }
  };

  const handleDrop = (event: React.DragEvent<HTMLDivElement>) => {
    event.preventDefault();
    setIsDragOver(false);

    if (props.disabled) {
      return;
    }

    const droppedFiles = event.dataTransfer.files;
    if (droppedFiles.length > 0) {
      const newFiles = Array.from(droppedFiles) as File[];
      const newUploadFiles = paresFiles(newFiles);
      const validatedFiles = validateFiles(newUploadFiles, props.existingFilesNames);
      props.onFilesSelected(validatedFiles);
    }
  };

  const resetSelectedFileInput = (event: React.ChangeEvent<HTMLInputElement>) => {
    event.target.value = "";
  };

  const validateFiles = (uploadFiles: UploadFile[], existingFilesNames?: string[]) => {
    return uploadFiles.map(uploadFile => {
      uploadFile.errors = uploadFile.errors ?? [];

      const uploadFileType = uploadFile.file.type?.length
        ? uploadFile.file.type
        : "." + uploadFile.file.name.split(".").pop() || ".*";

      if (uploadFile.file.size > props.fileSizeLimitMB * 1024 * 1024) {
        uploadFile.errors = [...uploadFile.errors, FileUploadError.MAX_FILE_SIZE_EXCEEDED];
      }

      if (
        !allowedFileTypes.some(fileType => fileType.value === uploadFileType) &&
        !allowedFileTypes.some(fileType => fileType.value === "video/*" && uploadFile.file.type.startsWith("video/")) &&
        !allowedFileTypes.some(fileType => fileType.value === "audio/*" && uploadFile.file.type.startsWith("audio/"))
      ) {
        uploadFile.errors = [...uploadFile.errors, FileUploadError.INVALID_FILE_TYPE];
      }
      if (existingFilesNames?.some(fileName => fileName === uploadFile.file.name)) {
        uploadFile.errors = [...uploadFile.errors, FileUploadError.FILE_ALREADY_EXISTS];
      }

      return uploadFile;
    });
  };

  const paresFiles = (files: File[]): UploadFile[] => {
    return files.map(
      file =>
        new UploadFile({
          id: uuidv4(),
          file,
          icon: getFileIcon(file),
          fileSizeInByte: file.size,
        }),
    );
  };

  const getFileIcon = (file: File): IconDefinition => {
    return allowedFileTypes.find((fileType: IFileType) => file.type === fileType.value)?.icon ?? faFile;
  };

  return (
    <div
      className={classNames("file-drop-area", {
        disabled: isUploadAreaDisabled,
        "drag-over": isDragOver,
      })}
      style={props.styles}
      onDrop={handleDrop}
      onDragOver={event => event.preventDefault()}
      onDragEnter={handleDragEnter}
      onDragLeave={handleDragLeave}
      onKeyDown={handleKeyDown}
      onClick={() => inputRef.current?.click()}
      tabIndex={0}
      role="button"
    >
      <div
        className={classNames("upload-info", {
          "small-area": props.smallUploadArea,
        })}
      >
        <label className="upload-icon-container" data-testid="file-upload-icon-button">
          <div className="upload-icon">
            <FontAwesomeIcon icon={faCloudArrowUp} />
          </div>
        </label>

        <div className="upload-text-container">
          <div className="upload-browse">
            <label className="upload-button-text" data-testid="file-upload-text-button">
              {t(translationKeys.upload.clickForUpload)}
            </label>
            &nbsp;
            <span>{t(translationKeys.upload.orDropFileIntoArea)}</span>
          </div>

          {props.customDescriptionText ? (
            <div>{props.customDescriptionText}</div>
          ) : (
            <Fragment>
              <div>{t(translationKeys.upload.allowedFileSize, { maxFileSize: props.fileSizeLimitText })}</div>

              {props.showAllowedFileTypes && (
                <div>
                  {t(translationKeys.upload.allowedFileTypesInformation)}
                  {": "}
                  {allowedFileTypes.map((fileType: IFileType, index: number) => (
                    <Fragment key={fileType.labelKey}>
                      {t(fileType.labelKey)}
                      {index !== allowedFileTypes.length - 1 && ", "}
                    </Fragment>
                  ))}
                </div>
              )}

              {!!props.maxConcurrentProcessedFiles && props.maxConcurrentProcessedFiles > 1 && (
                <div>
                  {t(translationKeys.upload.allowedMaxFiles)}: {props.maxConcurrentProcessedFiles}
                </div>
              )}
            </Fragment>
          )}
        </div>
      </div>

      <input
        ref={inputRef}
        type="file"
        hidden
        id="browse"
        onChange={handleFileChange}
        accept={allowedFileTypes.map(fileType => fileType.value).join(",")}
        multiple={!props.isSingleFileUpload}
        disabled={isUploadAreaDisabled}
      />
    </div>
  );
};

export const FileUploadArea = observer(RawFileUploadArea);
